import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Inbox, Loader2, Filter } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatCards from "@/components/admin/StatCards";
import PledgeRow from "@/components/admin/PledgeRow";

const typeLabels = {
  support: "Financial Support",
  partnership: "Partnership",
  volunteer: "Volunteer",
  prayer: "Prayer",
};

const statusColors = {
  new: "bg-brand-terracotta/15 text-brand-terracotta",
  reviewed: "bg-brand-sand/25 text-[#8a6a3a]",
  contacted: "bg-brand-green/15 text-brand-green",
};

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pledges, setPledges] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([api.get("/admin/pledges"), api.get("/admin/stats")]);
      setPledges(p.data);
      setStats(s.data);
    } catch {
      toast.error("Failed to load submissions.");
    } finally {
      setLoading(false);
    }
  }, [setPledges, setStats, setLoading]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/pledges/${id}`, { status });
      setPledges((prev) => prev.map((p) => (p.id === id ? { ...p, status } : p)));
      toast.success("Status updated.");
    } catch {
      toast.error("Update failed.");
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const filtered = filter === "all" ? pledges : pledges.filter((p) => p.interest_type === filter);

  return (
    <div className="min-h-screen bg-brand-surface" data-testid="admin-page">
      <header className="bg-brand-green text-brand-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl">Ministry Dashboard</h1>
            <p className="text-brand-bg/60 text-sm">{user?.email}</p>
          </div>
          <button
            onClick={doLogout} data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 border border-brand-bg/30 hover:bg-brand-bg/10 px-5 py-2.5 rounded-full text-sm transition-all"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Stats */}
        <StatCards stats={stats} />

        {/* Filter */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <Filter className="w-4 h-4 text-brand-muted" />
          {["all", "support", "partnership", "volunteer", "prayer"].map((f) => (
            <button
              key={f}
              data-testid={`admin-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-sm capitalize transition-all ${
                filter === f ? "bg-brand-green text-brand-bg" : "bg-brand-bg border border-brand-line text-brand-muted hover:border-brand-green"
              }`}
            >
              {f === "all" ? "All" : typeLabels[f]}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-brand-green" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-brand-muted" data-testid="admin-empty">
            <Inbox className="w-12 h-12 mx-auto mb-4 opacity-40" />
            <p>No submissions yet.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-pledge-list">
            {filtered.map((p) => (
              <PledgeRow key={p.id} p={p} changeStatus={changeStatus} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
